import { Core } from "@faire/design-tokens";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { path as c1CategoryPath } from "@faire/web-api/routes/category/c1CategoryUrlId";
import { path as c2CategoryPath } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrlString } from "@faire/web/common/transform-images";
import { Button } from "@faire/web/slate/Button";
import { Column, Grid } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import {
  TrackedLink,
  TrackedLinkContext,
} from "@faire/retailer/components/TrackedLink";
import { getAllBrandsLink } from "@faire/retailer/consts/allBrandsLinks";
import { getIsApparelRetailer } from "@faire/retailer/serialized-data/getIsApparelRetailer";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findActiveCategoryByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";

const useCategoryTiles = () => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      displayName: strictLocalize({
        defaultMessage: "Women's Accessories",
        description: { text: "Category name" },
      }),
      categoryUrlIds: getIsApparelRetailer()
        ? ["Accessories", "Women"]
        : ["Women", "Accessories"],
      backgroundImage:
        "https://cdn.faire.com/fastly/281d61dc17d79a0b2a70c8b69b8e0571b18fbf421e9b52978496defde549c71e.jpeg",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Paper & Novelty",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Paper & Novelty"],
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/stationery/1566265043.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Food & Drink",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Food & Drink"],
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/kitchen/1566265043.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Kids & Baby",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Kids & Baby"],
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/kids/1566265043.png",
    },
    {
      categoryUrlIds: getIsApparelRetailer()
        ? ["Apparel", "Women"]
        : ["Women", "Apparel"],
      displayName: strictLocalize({
        defaultMessage: "Women's Apparel",
        description: { text: "Category name" },
      }),
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/apparel/1566265043.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Jewelry",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Jewelry"],
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/jewelry/1566265044.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Beauty & Wellness",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Beauty & Wellness"],
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/beauty/1566265044.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Home Decor",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Home Decor"],
      backgroundImage:
        "https://cdn.faire.com/res/hszgttpjt/image/upload/loggedout-homepage/home/1566265043.png",
    },
  ];
};

const ShopOurCategoriesBase = React.forwardRef<
  HTMLDivElement,
  { className?: string }
>((props, ref) => {
  const isSSR = useIsSSR();
  const { categories } = useCategoryStore();
  const gridGap = useResponsiveValue({
    mobileAndAbove: 10,
    tabletAndAbove: 14,
  });
  const gridColumns = useResponsiveValue({
    mobileAndAbove: 1,
    tabletAndAbove: 2,
    desktopAndAbove: 4,
  });
  const ratio = useResponsiveValue({
    mobileAndAbove: 375 / 150,
    tabletAndAbove: 270 / 160,
  });
  const categoryTiles = useCategoryTiles();

  return (
    <CategoriesContainer className={props.className} ref={ref}>
      <CategoriesTitle>
        <StrictLocalMsg defaultMessage="Shop by category" />
      </CategoriesTitle>
      <TrackedLinkContext.Provider
        value={{
          type: IClickInfo.Type.BRAND,
          containerName: "page_not_found_category_grid",
        }}
      >
        <CategoriesGrid gap={gridGap} columns={gridColumns}>
          {categoryTiles.map((categoryTile) => {
            const categoryUrlIds = categoryTile.categoryUrlIds;
            const [c1, c2] = categoryUrlIds;
            if (
              !c1 ||
              findActiveCategoryByUrlIds(
                categoryTile.categoryUrlIds,
                categories
              ) === undefined
            ) {
              return null;
            }

            const categoryPath = c2
              ? c2CategoryPath(c1, c2)
              : c1CategoryPath(c1);
            const displayName = categoryTile.displayName;
            const token = categoryUrlIds.join("|");
            return (
              <CategoryWrapper key={token}>
                <AspectRatioPicture $width="100%" $ratio={ratio}>
                  <source
                    srcSet={optimizeImageUrlString(
                      categoryTile.backgroundImage,
                      isSSR,
                      {
                        width: 440,
                      }
                    )}
                    media="(min-width: 768px)"
                  />
                  <img
                    src={optimizeImageUrlString(
                      categoryTile.backgroundImage,
                      isSSR,
                      {
                        width: 750,
                      }
                    )}
                    alt={displayName}
                  />
                </AspectRatioPicture>
                <CategoryLink to={categoryPath} token={token}>
                  <CategoryButton>{displayName}</CategoryButton>
                </CategoryLink>
              </CategoryWrapper>
            );
          })}
        </CategoriesGrid>
        <SeeAllLink variant="tertiary" linkTo={getAllBrandsLink()}>
          <StrictLocalMsg defaultMessage="See All" />
        </SeeAllLink>
      </TrackedLinkContext.Provider>
    </CategoriesContainer>
  );
});
export const ShopOurCategories = observer(ShopOurCategoriesBase);

const CategoriesContainer = styled(Column).attrs({
  align: "center",
})`
  position: relative;
  background-color: ${Core.surface.inverse};
  width: 100%;

  padding: ${getSpacing("10x", 0)};
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("5x", 0)};
  }
`;

const CategoriesTitle = styled(Typography).attrs({
  variant: "displaySSerifRegular",
})`
  &&& {
    ${responsiveMediaQueries.mobile} {
      margin: ${getSpacing("4x", 0)};
    }
  }
`;

const CategoriesGrid = styled(Grid).attrs({
  columnTemplate: (c: number) => `repeat(${c}, minmax(0, 1fr));`,
})`
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${getSpacing("5x", 0)};
  max-width: calc(100vw - 40px);
  ${responsiveMediaQueries.mobile} {
    padding: unset;
    max-width: unset;
  }
`;

const CategoryWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CategoryLink = styled(TrackedLink)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border-bottom: none;
`;

const CategoryButton = styled(Typography).attrs({
  color: Core.text.default,
  align: "center",
})`
  background-color: ${Core.surface.inverse};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70%;
  height: 50px;
`;

const SeeAllLink = styled(Button)`
  ${responsiveMediaQueries.mobile} {
    margin: ${getSpacing("3x", 0)};
  }
`;

export const ShopOurCategoriesTitle = CategoriesTitle;
export const ShopOurCategoriesGrid = CategoriesGrid;
